<template>
  <section class="sbc-rooms" id="odalar">
    <div class="sbc-rooms__inner">
      <div class="sbc-rooms__head">
        <div>
          <div class="sbc-rooms__kicker">{{ $t('rooms_kicker') }}</div>
          <h2 class="sbc-rooms__title">{{ $t('rooms_title') }}</h2>
        </div>
        <NuxtLink class="sbc-rooms__cta" :to="redirect('rooms')" aria-label="See our rooms">
          {{ $t('view_all_rooms') }}
        </NuxtLink>
      </div>
      <p class="sbc-rooms__desc">{{ $t('rooms_section_desc') }}</p>

      <!-- Loading: responsive skeleton cards, shown until rooms arrive -->
      <div v-if="loading" class="sbc-rooms__skeleton" aria-hidden="true">
        <div class="sbc-rooms__track sbc-rooms__track--skeleton">
          <div v-for="n in 4" :key="n" class="sbc-room sbc-room--skeleton">
            <div class="sbc-room__media sbc-skel"></div>
            <div class="sbc-room__body">
              <div class="sbc-skel sbc-skel--name"></div>
              <div class="sbc-room__row">
                <div class="sbc-skel sbc-skel--meta"></div>
                <div class="sbc-skel sbc-skel--cta"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template v-else>
      <!-- Desktop: one continuous carousel with click arrows -->
      <div class="sbc-rooms__viewport sbc-rooms__viewport--desktop">
        <div class="sbc-rooms__track" ref="trackEl" @scroll="onTrackScroll" v-reveal>
          <article v-for="(room, index) in rooms" :key="index" class="sbc-room" :style="{ '--i': index }">
            <div
              class="sbc-room__media"
              :class="{ 'is-revealed': revealedSlug === room.slug }"
              @click="toggleReveal(room)"
            >
              <NuxtImg format="webp" :src="img(room.cover_small)" :alt="room.title" loading="lazy" />
              <div class="sbc-room__media-overlay">
                <button
                  v-if="roomPhotos(room).length"
                  type="button"
                  class="sbc-room__media-btn"
                  @click.stop="openGallery(room)"
                >
                  {{ $t('view_gallery') }}
                </button>
                <NuxtLink
                  :to="`${redirect('rooms')}/${room.slug}`"
                  class="sbc-room__media-btn"
                  @click.stop
                >
                  {{ $t('detail') }}
                </NuxtLink>
              </div>
            </div>
            <div class="sbc-room__body">
              <h3 class="sbc-room__name">{{ room.title }}</h3>
              <div class="sbc-room__row">
                <p v-if="room.field" class="sbc-room__meta">{{ room.field }}</p>
                <div class="sbc-room__actions">
                  <NuxtLink :to="`${redirect('rooms')}/${room.slug}`" class="sbc-room__cta">
                    {{ $t('book_now') }}
                  </NuxtLink>
                  <button
                    v-if="roomPhotos(room).length"
                    type="button"
                    class="sbc-room__gallery-btn"
                    :aria-label="$t('view_gallery')"
                    @click="openGallery(room)"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>

        <button
          v-if="!atStart"
          type="button"
          class="sbc-rooms__arrow sbc-rooms__arrow--prev"
          aria-label="Previous"
          @click="scrollByCard(-1)"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <button
          v-if="!atEnd"
          type="button"
          class="sbc-rooms__arrow sbc-rooms__arrow--next"
          aria-label="Next"
          @click="scrollByCard(1)"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>

      <!-- Mobile: split into 3 categorized rows, each independently swipeable
           and with its own click arrows -->
      <div class="sbc-rooms__groups">
        <div v-for="(group, gi) in roomGroups" :key="group.key" class="sbc-rooms__group">
          <h3 class="sbc-rooms__group-title">{{ group.label }}</h3>
          <div class="sbc-rooms__group-viewport">
            <div
              class="sbc-rooms__track sbc-rooms__track--group"
              ref="groupTracks"
              @scroll="updateGroupScrollState(gi)"
              v-reveal
            >
              <article v-for="(room, index) in group.rooms" :key="index" class="sbc-room" :style="{ '--i': index }">
                <div
                  class="sbc-room__media"
                  :class="{ 'is-revealed': revealedSlug === room.slug }"
                  @click="toggleReveal(room)"
                >
                  <NuxtImg format="webp" :src="img(room.cover_small)" :alt="room.title" loading="lazy" />
                  <div class="sbc-room__media-overlay">
                    <button
                      v-if="roomPhotos(room).length"
                      type="button"
                      class="sbc-room__media-btn"
                      @click.stop="openGallery(room)"
                    >
                      {{ $t('view_gallery') }}
                    </button>
                    <NuxtLink
                      :to="`${redirect('rooms')}/${room.slug}`"
                      class="sbc-room__media-btn"
                      @click.stop
                    >
                      {{ $t('detail') }}
                    </NuxtLink>
                  </div>
                </div>
                <div class="sbc-room__body">
                  <h3 class="sbc-room__name">{{ room.title }}</h3>
                  <div class="sbc-room__row">
                    <p v-if="room.field" class="sbc-room__meta">{{ room.field }}</p>
                    <div class="sbc-room__actions">
                      <NuxtLink :to="`${redirect('rooms')}/${room.slug}`" class="sbc-room__cta">
                        {{ $t('book_now') }}
                      </NuxtLink>
                      <button
                        v-if="roomPhotos(room).length"
                        type="button"
                        class="sbc-room__gallery-btn"
                        :aria-label="$t('view_gallery')"
                        @click="openGallery(room)"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <button
              v-if="groupScroll[gi] && !groupScroll[gi].atStart"
              type="button"
              class="sbc-rooms__arrow sbc-rooms__arrow--prev sbc-rooms__arrow--group"
              aria-label="Previous"
              @click="scrollGroupByCard(gi, -1)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            <button
              v-if="groupScroll[gi] && !groupScroll[gi].atEnd"
              type="button"
              class="sbc-rooms__arrow sbc-rooms__arrow--next sbc-rooms__arrow--group"
              aria-label="Next"
              @click="scrollGroupByCard(gi, 1)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>
        </div>
      </div>
      </template>

      <p class="sbc-rooms__closing">{{ $t('rooms_closing') }}</p>
    </div>
    <GalleryModal v-model="galleryOpen" :photos="galleryPhotos" :detail-url="galleryDetailUrl" />
  </section>
</template>

<script>
export default {
  name: "RoomSection",
  data() {
    return {
      rooms: null,
      loading: true,
      atStart: true,
      atEnd: false,
      groupScroll: [],
      galleryOpen: false,
      galleryPhotos: [],
      galleryDetailUrl: null,
      // Which room's image-overlay (gallery/detail buttons) is showing.
      // Desktop reveals on CSS :hover regardless of this; it only drives
      // the tap-to-reveal behavior on touch devices, which have no hover.
      revealedSlug: null,
    };
  },
  computed: {
    // No category field on the room API — grouped by keyword in the title
    // for the mobile-only split view (villa / garden+park / everything else).
    roomGroups() {
      const list = this.rooms || [];
      const isVilla = (r) => /villa/i.test(r.title || "");
      const isGardenPark = (r) => /garden|park/i.test(r.title || "");
      const villas = list.filter(isVilla);
      const gardenPark = list.filter((r) => isGardenPark(r) && !isVilla(r));
      const seaView = list.filter((r) => !isVilla(r) && !isGardenPark(r));
      const isEn = this.language === "en";
      return [
        { key: "sea", label: isEn ? "Sea View Rooms" : "Deniz Manzaralı Odalar", rooms: seaView },
        { key: "villa", label: isEn ? "Villas" : "Villalar", rooms: villas },
        { key: "garden", label: isEn ? "Garden & Park Rooms" : "Garden ve Park Odaları", rooms: gardenPark },
      ].filter((g) => g.rooms.length > 0);
    },
  },
  setup() {
    const { locale, setLocale } = useI18n();

    const language = computed({
      get: () => locale.value,
      set: (value) => {
        setLocale(value);
      },
    });
    return {
      language,
    };
  },
  methods: {
    async getRooms() {
      await $fetch(
        `https://api2.salamisbayconti.com/api/general/${this.language}/room`,
        {
          method: "GET",
        }
      )
        .then((res) => {
          this.rooms = res;
          this.loading = false;
          this.$nextTick(() => {
            this.onTrackScroll();
            this.initGroupScrollState();
          });
        })
        .catch((e) => {
          console.log(e);
          this.loading = false;
        });
    },
    img(photo) {
      return `https://api2.salamisbayconti.com/storage/${photo}`;
    },
    // `room.photos` is a JSON-encoded string on both the list and detail
    // room endpoints (same shape pages/rooms/[slug].vue already parses) —
    // reused here so each card's gallery icon works off data already in
    // the list response, no extra per-room fetch needed.
    roomPhotos(room) {
      if (!room || !room.photos) return [];
      try {
        const parsed = typeof room.photos === "string" ? JSON.parse(room.photos) : room.photos;
        return parsed.map((p) => ({ image: this.img(p) }));
      } catch (e) {
        return [];
      }
    },
    openGallery(room) {
      this.galleryPhotos = this.roomPhotos(room);
      this.galleryDetailUrl = `${this.redirect('rooms')}/${room.slug}`;
      this.galleryOpen = true;
    },
    // Touch devices have no :hover, so tapping the image toggles the
    // overlay open instead of navigating immediately — a second tap on
    // one of its two buttons then does the actual gallery/detail action.
    toggleReveal(room) {
      this.revealedSlug = this.revealedSlug === room.slug ? null : room.slug;
    },
    redirect(route) {
      if (this.language === 'en') {
        return `/en/${route}`;
      }
      return `/${route}`;
    },
    onTrackScroll() {
      const el = this.$refs.trackEl;
      if (!el) return;
      this.atStart = el.scrollLeft <= 4;
      this.atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    },
    scrollByCard(dir) {
      const el = this.$refs.trackEl;
      if (!el) return;
      const card = el.querySelector(".sbc-room");
      const cardWidth = card ? card.getBoundingClientRect().width : 400;
      el.scrollBy({ left: dir * (cardWidth + 22), behavior: "smooth" });
    },
    // Same idea as atStart/atEnd above, but per mobile group row — each
    // row scrolls and reaches its own end independently of the others.
    initGroupScrollState() {
      this.groupScroll = this.roomGroups.map(() => ({ atStart: true, atEnd: false }));
      this.$nextTick(() => {
        const tracks = this.$refs.groupTracks;
        (Array.isArray(tracks) ? tracks : tracks ? [tracks] : []).forEach((_, i) =>
          this.updateGroupScrollState(i)
        );
      });
    },
    updateGroupScrollState(i) {
      const tracks = this.$refs.groupTracks;
      const el = Array.isArray(tracks) ? tracks[i] : tracks;
      if (!el) return;
      if (!this.groupScroll[i]) this.groupScroll[i] = { atStart: true, atEnd: false };
      this.groupScroll[i].atStart = el.scrollLeft <= 4;
      this.groupScroll[i].atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    },
    scrollGroupByCard(i, dir) {
      const tracks = this.$refs.groupTracks;
      const el = Array.isArray(tracks) ? tracks[i] : tracks;
      if (!el) return;
      const card = el.querySelector(".sbc-room");
      const cardWidth = card ? card.getBoundingClientRect().width : 300;
      el.scrollBy({ left: dir * (cardWidth + 22), behavior: "smooth" });
    },
  },

  mounted() {
    this.getRooms();
    this.onTrackScroll();
    window.addEventListener("resize", this.onTrackScroll);
    window.addEventListener("resize", this.initGroupScrollState);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onTrackScroll);
    window.removeEventListener("resize", this.initGroupScrollState);
  },
};
</script>

<style scoped>
.sbc-rooms {
  --sbc-navy-900: #0B1F2E;
  --sbc-gold-400: #D9B96B;
  --sbc-gold-600: #C19A45;
  background: #F5EDDF;
  padding: clamp(56px, 6vw, 100px) clamp(20px, 4vw, 48px);
  border-top: 2px solid var(--sbc-gold-600);
  border-bottom: 2px solid var(--sbc-gold-600);
}
.sbc-rooms__inner { max-width: 1320px; margin: 0 auto; }

.sbc-rooms__head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 32px; flex-wrap: wrap;
}
.sbc-rooms__kicker {
  font-family: "Cormorant Garamond", Georgia, serif; font-size: .8125rem;
  letter-spacing: .28em; text-transform: uppercase; color: var(--sbc-navy-900);
  font-weight: 700; text-shadow: 0 2px 6px rgba(11,31,46,.35);
}
.sbc-rooms__title {
  font-family: "Cormorant Garamond", Georgia, serif; font-weight: 300;
  font-size: clamp(2rem, 3.6vw, 3.25rem); line-height: 1.15;
  color: var(--sbc-navy-900); margin: 16px 0 0;
}
.sbc-rooms__desc {
  font-family: "Jost", sans-serif; font-size: clamp(17px, 1.35vw, 21px);
  font-weight: 300; line-height: 1.75; color: rgba(11,31,46,.72);
  max-width: 720px; margin: 20px 0 clamp(36px, 4vw, 56px);
}
.sbc-rooms__closing {
  font-family: "Jost", sans-serif; font-size: 1rem;
  font-weight: 300; line-height: 1.7; color: rgba(11,31,46,.65);
  max-width: none; margin: clamp(28px, 3vw, 40px) 0 0;
}
.sbc-rooms__cta {
  flex-shrink: 0;
  font-size: .8125rem; font-weight: 700; letter-spacing: .16em; text-transform: uppercase;
  color: var(--sbc-navy-900); text-decoration: none;
  border-bottom: 1px solid var(--sbc-gold-600); padding-bottom: 3px;
  transition: color 200ms ease, border-color 200ms ease;
}
.sbc-rooms__cta:hover { color: var(--sbc-gold-600); border-color: var(--sbc-navy-900); }

.sbc-rooms__viewport { position: relative; }
.sbc-rooms__groups { display: none; }
.sbc-rooms__group { margin-bottom: 32px; }
.sbc-rooms__group:last-child { margin-bottom: 0; }
.sbc-rooms__group-title {
  font-family: "Cormorant Garamond", Georgia, serif; font-weight: 500; font-size: 1.1rem;
  letter-spacing: .08em; text-transform: uppercase; color: var(--sbc-navy-900);
  margin: 0 0 14px;
}
.sbc-rooms__group-viewport { position: relative; }
.sbc-rooms__arrow--group { top: 90px; width: 38px; height: 38px; }
.sbc-rooms__arrow--group.sbc-rooms__arrow--prev { left: -4px; }
.sbc-rooms__arrow--group.sbc-rooms__arrow--next { right: -4px; }
.sbc-rooms__track {
  display: flex;
  flex-wrap: nowrap;
  gap: 22px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.sbc-rooms__track::-webkit-scrollbar { display: none; }

.sbc-rooms__arrow {
  position: absolute; top: 98px;
  width: 44px; height: 44px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: rgba(11,31,46,.06); border: 1px solid rgba(11,31,46,.24);
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  color: var(--sbc-navy-900); cursor: pointer; z-index: 3;
  transition: background 200ms ease, border-color 200ms ease, color 200ms ease;
}
.sbc-rooms__arrow:hover {
  background: var(--sbc-gold-400); border-color: var(--sbc-gold-400); color: var(--sbc-navy-900);
}
.sbc-rooms__arrow--prev { left: -8px; }
.sbc-rooms__arrow--next { right: -8px; }

.sbc-room {
  display: flex; flex-direction: column; flex: 0 0 auto; width: 400px;
  scroll-snap-align: start;
}
/* Same as EventsSection: card images zoom in one after another. Uses the
   standalone `scale` property so it composes with the hover `transform`. */
.sbc-rooms__track[data-reveal="in"] > .sbc-room .sbc-room__media :deep(img) {
  transition:
    transform 520ms cubic-bezier(.22,.61,.36,1),
    scale 2200ms cubic-bezier(.2, .7, .2, 1),
    opacity 1400ms ease;
  transition-delay: 0s, calc(min(var(--i, 0), 8) * 320ms), calc(min(var(--i, 0), 8) * 320ms);
}
.sbc-rooms__track[data-reveal="out"] > .sbc-room .sbc-room__media :deep(img) {
  scale: 1.3;
  opacity: 0;
}
.sbc-room__media {
  position: relative; display: block; cursor: pointer;
  height: 260px; /* trimmed from 300px so the section isn't near full viewport height */
  border-radius: 4px; overflow: hidden; background: #14293a;
}
.sbc-room__media :deep(img) {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 520ms cubic-bezier(.22,.61,.36,1);
}
.sbc-room:hover .sbc-room__media :deep(img) { transform: scale(1.04); }

/* Hover (desktop) / tap via .is-revealed (touch — see toggleReveal) dims
   the photo and surfaces the gallery/detail actions over it. */
.sbc-room__media-overlay {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
  background: rgba(11,31,46,.5);
  opacity: 0; pointer-events: none;
  transition: opacity 250ms ease;
}
.sbc-room__media:hover .sbc-room__media-overlay,
.sbc-room__media.is-revealed .sbc-room__media-overlay {
  opacity: 1; pointer-events: auto;
}
.sbc-room__media-btn {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 150px; padding: 9px 20px;
  background: rgba(255,255,255,.96); color: var(--sbc-navy-900);
  font-size: .75rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
  text-decoration: none; border: none; border-radius: 2px; cursor: pointer;
  transition: background 200ms ease, color 200ms ease;
}
.sbc-room__media-btn:hover { background: var(--sbc-gold-400); }

.sbc-room__body {
  flex: 1;
  padding: 24px 4px 0; display: flex; flex-direction: column; gap: 8px;
}
.sbc-room__name {
  font-family: "Cormorant Garamond", Georgia, serif; font-size: 1rem; letter-spacing: .16em;
  font-weight: 700; text-transform: uppercase; color: var(--sbc-navy-900); margin: 0;
}
.sbc-room__row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  margin-top: auto; padding-top: 12px;
}
.sbc-room__meta {
  margin: 0; font-size: .875rem; line-height: 1.7;
  color: rgba(11,31,46,.68);
}
.sbc-room__cta {
  font-size: .75rem; letter-spacing: .16em; text-transform: uppercase;
  color: var(--sbc-navy-900); text-decoration: none;
  border-bottom: 1px solid var(--sbc-gold-600); padding-bottom: 2px;
  transition: color 200ms ease, border-color 200ms ease;
}
.sbc-room__cta:hover { color: var(--sbc-gold-600); border-color: var(--sbc-gold-600); }
.sbc-room__actions {
  display: flex; align-items: center; gap: 10px;
}
.sbc-room__gallery-btn {
  display: flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; padding: 0;
  background: none; border: 1px solid rgba(11,31,46,.22); border-radius: 50%;
  color: var(--sbc-navy-900); cursor: pointer;
  transition: color 200ms ease, border-color 200ms ease, background 200ms ease;
}
.sbc-room__gallery-btn:hover {
  color: var(--sbc-gold-600); border-color: var(--sbc-gold-600); background: rgba(217,185,107,.12);
}

/* Skeleton loading state — reuses .sbc-room / .sbc-room__media sizing so it
   stays in sync with the real cards' responsive dimensions. */
.sbc-rooms__skeleton { position: relative; }
.sbc-rooms__track--skeleton { overflow: hidden; }
.sbc-room--skeleton { cursor: default; }
.sbc-skel {
  position: relative;
  overflow: hidden;
  background: rgba(11,31,46,.08);
  border-radius: 4px;
}
.sbc-skel::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent, rgba(11,31,46,.10), transparent);
  transform: translateX(-100%);
  animation: sbc-skel-shimmer 1.5s ease-in-out infinite;
}
@keyframes sbc-skel-shimmer {
  100% { transform: translateX(100%); }
}
.sbc-skel--name { height: 14px; width: 55%; margin-top: 2px; }
.sbc-skel--meta { height: 12px; width: 35%; }
.sbc-skel--cta { height: 12px; width: 28%; }

@media (prefers-reduced-motion: reduce) {
  .sbc-skel::after { animation: none; }
}

@media (min-width: 768px) {
  .sbc-rooms {
    padding-block: clamp(44px, 4vw, 72px);
  }
  .sbc-room__media {
    height: 280px;
  }
  .sbc-room__body {
    padding-top: 18px;
  }
}

@media (max-width: 767px) {
  .sbc-rooms__head { align-items: flex-start; }
  .sbc-rooms__kicker {
    font-size: .6875rem;
    letter-spacing: .2em;
  }
  .sbc-rooms__title {
    font-size: clamp(2.25rem, 11vw, 3.25rem);
    line-height: 1.05;
    margin-top: 12px;
  }
  .sbc-rooms__desc {
    font-size: 1.0625rem;
    line-height: 1.55;
    margin-top: 16px;
  }
  .sbc-rooms__cta {
    margin-top: 4px;
    font-size: .6875rem;
    letter-spacing: .14em;
  }
  .sbc-room { width: calc(100vw - 62px); }
  .sbc-room__media { height: 220px; }

  /* Swap the single desktop carousel for 3 categorized swipeable rows. */
  .sbc-rooms__viewport--desktop { display: none; }
  .sbc-rooms__groups { display: block; }
}
/* Mobile: +10px vertical padding */
@media (max-width: 767px) {
  .sbc-rooms { padding-block: calc(clamp(56px, 6vw, 100px) + 10px); }
}
</style>
